import { observable, action, extendObservable } from 'mobx';
import decode from 'lib/html/entities/decode';
import computed from 'lib/mobx/computed';
import JSONSerializable from 'lib/serialization/json-serializable';
import { ApiItem } from 'lib/api/api-item';
import { WPAttachment } from 'lib/wp/wp-attachment';

import { ComplaintStore } from 'four-nets/admin/admin';
import { ApiStore } from 'four-nets/api/wp-api-store';
import { UserStore } from 'four-nets/user/user';
import { WPLikeStore } from 'four-nets/like/like';
import Likeable from 'four-nets/like/likeable';
import { DeletedContent } from 'four-nets/common/deleted-content';

export { Category };

@JSONSerializable()
class Category extends ApiItem {
    @observable featuredMedia;

    fromJSON({ json }) {
        const { featuredMedia: featuredMediaJSON, ...rest } = json;

        super.fromJSON({ json: rest });

        if (featuredMediaJSON) {
            this.featuredMedia = new WPAttachment({});
            this.featuredMedia.fromJSON({ json: featuredMediaJSON });
        }
    }
}

export class CategoryStore extends ApiStore<Category> {
    @observable categories = [];

    constructor(options) {
        super(options);
        this.delegate = this;
    }

    item() {
        return new Category({
            delegate: this,
        });
    }

    loadAll(query) {
        const request = this.api.get({
            path: `magazine-categories`,
        });

        const categories = [];

        if (query) {
            request.query(query);
        }

        const loadAllDidSucceed = result => {
            categories.push(...result.results);

            if (result.loadNext) {
                return result.loadNext({ categories }).then(loadAllDidSucceed);
            }

            return categories;
        };

        return super
            .loadAll({
                request,
                categories,
            })
            .then(loadAllDidSucceed);
    }
}

export { Post };

@Likeable()
@JSONSerializable()
class Post extends ApiItem {
    categoryStore: CategoryStore;
    commentStore: CommentStore;
    userStore: UserStore;

    @observable date: Date;
    @observable comments: WordpressResult<Comment>;
    @observable categories: Category[];
    @observable featuredMedia: ?WPImage;

    constructor(options: { categoryStore: CategoryStore, commentStore: CommentStore, userStore: UserStore }) {
        super(options);

        const { categoryStore, commentStore, userStore } = options;

        this.categoryStore = categoryStore;
        this.commentStore = commentStore;
        this.userStore = userStore;
        this.likeStore = new WPLikeStore({
            userStore,
        });
    }

    @action loadCommentsDidSucceed = comments => {
        return comments;
    };

    @action loadCommentsDidFinish = () => {
        this.loadingComments = false;
    };

    @action loadComments(query) {
        this.loadingComments = true;

        return this.commentStore
            .loadAll(this, query)
            .then(this.loadCommentsDidSucceed)
            .finally(this.loadCommentsDidFinish);
    }

    @action createComment({ content }) {
        return this.commentStore.create({
            post: this,
            content,
        });
    }

    @action incrementReadCount() {
        this.delegate.incrementReadCount(this);
    }

    fromJSON({ json }) {
        const {
            featuredMedia: featuredMediaJSON,
            gallery: galleryJSON,
            attachedMedia: attachedMediaJSON,
            categories: categoriesJSON,
            date,
            ...rest
        } = json;

        super.fromJSON({ json: rest });

        if (date) {
            this.date = new Date(date);
        }

        if (this.title) {
            extendObservable(this.title, {
                rendered: decode(this.title.rendered),
            });
        }

        if (this.excerpt) {
            extendObservable(this.excerpt, {
                rendered: decode(this.excerpt.rendered),
            });
        }

        if (featuredMediaJSON) {
            if (!this.featuredMedia) {
                this.featuredMedia = new WPAttachment({});
            }

            this.featuredMedia.fromJSON({ json: featuredMediaJSON });
        }

        if (categoriesJSON) {
            this.categories = this.categoryStore.processPlainJSONArray({ json: categoriesJSON });
        }

        if (galleryJSON) {
            this.gallery = galleryJSON.map(j => {
                const attachment = new WPAttachment({});
                return attachment.fromJSON({ json: j });
            });
        }

        if (attachedMediaJSON) {
            this.attachedMedia = attachedMediaJSON.map(j => {
                const attachment = new WPAttachment({});
                return attachment.fromJSON({ json: j });
            });
        }
    }

    @computed get attachment() {
        const result = {};

        const { gallery = [], attachedMedia = [] } = this;

        [...gallery, ...attachedMedia].forEach(item => {
            result[item.id] = item;
        });

        return result;
    }
}

export class PostStore extends ApiStore {
    categoryStore: CategoryStore;
    commentStore: CommentStore;

    @observable loadingMostRead = false;

    constructor(options) {
        super(options);

        const { categoryStore, userStore, api, intl } = options;

        this.categoryStore = categoryStore;
        this.delegate = this;
        this.userStore = userStore;
        this.intl = intl;

        this.commentStore = new CommentStore({
            api,
            postStore: this,
            userStore,
            intl,
        });
    }

    item() {
        return new Post({
            categoryStore: this.categoryStore,
            commentStore: this.commentStore,
            delegate: this,
            userStore: this.userStore,
            intl: this.intl,
        });
    }

    loadAll(options: { category: Category, query: ?Object }) {
        const { category, query } = options || {};

        const request = this.api.get({
            path: `magazine-posts`,
        });

        if (category) {
            request.query({
                categories: category.id,
            });
        }

        if (query) {
            request.query(query);
        }

        return super.loadAll({
            request,
        });
    }

    load(options: { id: number }) {
        const { id } = options;
        const request = this.api.get({
            path: `magazine-posts/${id}`,
        });

        return super.load({
            id,
            request,
        });
    }

    @action loadMostReadDidFinish = () => {
        this.loadingMostRead = false;
    };

    @action loadMostReadDidSucceed = mostRead => {
        this.mostRead = mostRead;
        return mostRead;
    };

    loadLikes(options) {
        const { likeable: post } = options;

        const request = this.api.get({
            path: `magazine-posts/${post.id}/likes`,
        });

        return request.json().then(post.likeStore.processJSONArray);
    }

    @action incrementReadCount(post) {
        const request = this.api.post({
            path: `magazine-posts/${post.id}/read-count`,
        });

        request.end(() => null);
    }

    like(options) {
        const { likeable: post } = options;

        const request = this.api.post({
            path: `magazine-posts/${post.id}/like`,
        });

        return request
            .json()
            .then(result => this.processJSON({ json: { id: result.json.id, likerIds: result.json.likerIds } })); // this is a patch for unconsistent data returned from api, because it will be deprecated
    }

    unlike(options) {
        const { likeable: post } = options;

        const request = this.api.post({
            path: `magazine-posts/${post.id}/unlike`,
        });

        return request
            .json()
            .then(result => this.processJSON({ json: { id: result.json.id, likerIds: result.json.likerIds } }));
    }

    @action loadMostRead(query) {
        this.loadingMostRead = true;
        const params = { per_page: 6, order: 'desc', orderbymeta: 'read_count', ...query };
        return this.loadAll({ query: params }).then(this.loadMostReadDidSucceed).finally(this.loadMostReadDidFinish);
    }
}

export { Comment };

@Likeable()
@JSONSerializable()
class Comment extends ApiItem {
    postStore: PostStore;
    userStore: UserStore;
    likePromise: ?Promise<Comment>;

    @observable loadingLikes = false;

    @observable date;

    constructor(options) {
        super(options);

        const { postStore, userStore, intl } = options;
        this.postStore = postStore;
        this.userStore = userStore;

        this.likeStore = new WPLikeStore({
            userStore,
        });

        this.userCanEdit = false;
        this.intl = intl;
    }

    fromJSON({ json }) {
        const { user: userJSON, date, ...rest } = json;

        this.date = new Date(date);

        super.fromJSON({
            json: rest,
        });

        this.user = this.userStore.processJSON({ json: userJSON });

        extendObservable(this.content, {
            rendered: decode(this.content.rendered),
        });
    }

    complain({ message } = {}) {
        return this.delegate.complain({ blogComment: this, comment: message });
    }

    @computed get time() {
        return this.date;
    }

    @computed get body() {
        if (this.content) {
            return this.content.rendered;
        }

        return null;
    }

    @computed get displayedContent() {
        if (this.status === 'approved') {
            return this.body;
        }
        return new DeletedContent('', this.intl);
    }

    mailSubject({ intl }) {
        if (this.post) {
            return `Magazín -> ${this.post.title.rendered}`;
        }

        return null;
    }

    updateContent(content) {
        return this.delegate.updateContent(this, content);
    }

    delete() {
        return this.delegate.delete(this);
    }
}

export { CommentStore };

@JSONSerializable()
class CommentStore extends ApiStore {
    postStore: PostStore;
    userStore: UserStore;

    constructor(options) {
        super(options);

        const { postStore, userStore, intl } = options;

        this.delegate = this;
        this.postStore = postStore;
        this.userStore = userStore;
        this.complaintStore = new ComplaintStore(options);
        this.intl = intl;
    }

    item() {
        return new Comment({
            postStore: this.postStore,
            userStore: this.userStore,
            delegate: this,
            intl: this.intl,
        });
    }

    create(options) {
        const { post, content } = options;

        const request = this.api.post({
            path: `magazine-posts/${post.id}/comments`,
        });

        request.send({
            post: post.id,
            content,
            date: new Date(),
        });

        return super.create({
            request,
        });
    }

    updateContent(comment, content) {
        return this.api
            .patch({
                path: `magazine-posts/${comment.post.id}/comments/${comment.id}`,
            })
            .send({ content })
            .json()
            .then(result => this.processJSON(result));
    }

    delete(comment) {
        return this.api
            .del({
                path: `magazine-posts/${comment.post.id}/comments/${comment.id}`,
            })
            .json()
            .then(result => this.processJSON(result));
    }

    loadAll(post, query = {}) {
        const request = this.api.get({
            path: `magazine-posts/${post.id}/comments`,
        });

        // if (!query.order) {
        //     query.order = 'asc';
        // }

        request.query(query);

        return super.loadAll({
            request,
            post,
        });
    }

    loadLikes(options) {
        const { likeable: comment } = options;

        const request = this.api.get({
            path: `magazine-posts/${comment.post}/comments/${comment.id}/likes`,
        });

        return request.json().then(comment.likeStore.processJSONArray);
    }

    like(options) {
        const { likeable: comment } = options;

        const request = this.api.post({
            path: `magazine-posts/${comment.post}/comments/${comment.id}/like`,
        });

        return request.json().then(result => this.processJSON(result));
    }

    unlike(options) {
        const { likeable: comment } = options;

        const request = this.api.post({
            path: `magazine-posts/${comment.post}/comments/${comment.id}/unlike`,
        });

        return request.json().then(result => this.processJSON(result));
    }

    complain({ comment, blogComment }: { blogComment: Comment, comment?: string }) {
        return this.complaintStore.create({
            comment,
            objectId: blogComment.id,
            appLabel: 'blog',
            model: 'comment',
        });
    }
}
